import React from 'react';
import { StyleSheet, View } from 'react-native';
import { colors, hairline, radii, screenPadding, spacing } from '../../theme';
import Button from './Button';
import Icon from './Icon';
import Text from './Text';

/**
 * A request failed, and the screen has nothing to show because of it.
 *
 * ---------------------------------------------------------------------------
 * NOT THE SAME THING AS EMPTY
 * ---------------------------------------------------------------------------
 * "You have no cancelled bookings" and "we could not reach the server" look
 * identical if both are drawn as an empty list, and they are opposite facts:
 * one is the answer, the other is the absence of one. Conflating them is how
 * an outage gets reported as "the app says I have no bookings" - and then
 * someone books the same job twice.
 *
 * So this is deliberately a different shape from `EmptyState`: danger ink, and
 * always a way to try again. Every error a customer sees must end in something
 * they can do.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT SAYS, AND WHAT IT DOES NOT
 * ---------------------------------------------------------------------------
 * The headline is plain language. The server's own message goes underneath it
 * only when there is one worth reading - "Coupon has expired" helps; "Request
 * failed with status code 500" does not, and neither does a stack trace.
 *
 * `detail` is for the code support will ask for. It is `selectable` for the
 * same reason the payment failure code is: people read codes aloud and get
 * them wrong.
 */

type Props = {
  /** Overrides the generic headline when the screen knows better. */
  title?: string;
  /**
   * One line on what went wrong, from the server where it is worth showing.
   * Falls back to the connection line, which is true far more often than not.
   */
  message?: string;
  /** A code or reference for support. Shown small, and selectable. */
  detail?: string;
  onRetry?: () => void;
  retryLabel?: string;
  /**
   * Sits inside a card rather than filling the screen.
   *
   * For a band that failed inside a page that otherwise loaded - one rail out
   * of four. Taking the whole screen for that would throw away the three
   * things that did work.
   */
  compact?: boolean;
};

function ErrorState({
  title = "That didn't load",
  message = 'Check your connection and try again.',
  detail,
  onRetry,
  retryLabel = 'Try again',
  compact = false,
}: Props) {
  return (
    <View
      style={compact ? styles.compact : styles.root}
      accessibilityRole="alert"
      // Read as one announcement rather than four fragments, and read the
      // headline first - a screen reader landing on "Try again" with no idea
      // what failed is worse than silence.
      accessible
      accessibilityLabel={[title, message, detail].filter(Boolean).join('. ')}
    >
      <View style={compact ? styles.markSm : styles.mark}>
        <Icon
          name="xCircle"
          size={compact ? 20 : 30}
          color={colors.danger}
          strokeWidth={1.5}
        />
      </View>

      <Text variant={compact ? 'labelSm' : 'subtitle'} align="center">
        {title}
      </Text>

      <Text variant="bodySm" align="center">
        {message}
      </Text>

      {detail ? (
        <Text
          variant="eyebrow"
          align="center"
          color={colors.inkMuted}
          selectable
        >
          {detail}
        </Text>
      ) : null}

      {onRetry ? (
        <Button
          label={retryLabel}
          variant="outline"
          size={compact ? 'sm' : 'md'}
          fullWidth={false}
          leadingIcon="refresh"
          onPress={onRetry}
          style={styles.action}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.xxxl,
  },
  compact: {
    alignItems: 'center',
    gap: spacing.sm,
    marginHorizontal: screenPadding,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  mark: {
    width: 60,
    height: 60,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.dangerSurface,
    marginBottom: spacing.xs,
  },
  markSm: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.dangerSurface,
  },
  action: {
    marginTop: spacing.sm,
    // Same fix as EmptyState's: `fullWidth={false}` sets `alignSelf:
    // 'flex-start'`, which beats this view's `alignItems: 'center'`.
    alignSelf: 'center',
  },
});

export default ErrorState;
